import { FC } from 'react';
import * as vp from 'vopay';
import AccountingSoftwareLayout from '../AccountingSoftwareLayout/AccountingSoftwareLayout';
import styles from './ChartOfAccounts.module.scss';
import { AccountingImageMap } from './AccountingImageMap';
import { ChartOfAccountsList } from '../types';
import { useTranslation } from 'react-i18next';

type ChartOfAccountsProps = {
  onSave: () => void;
  serviceName?: vp.RailzServiceNames;
  chartsReceivables: ChartOfAccountsList;
  chartsPayables: ChartOfAccountsList;
  currentChartReceivable: string;
  currentChartPayable: string;
  originalChartPayables: string;
  originalChartReceivable: string;
  onReceivableChange: (value: string) => void;
  onPayableChange: (value: string) => void;
};
const { Box, Title, SubTitle, Select, Button } = vp;
const ChartOfAccounts: FC<ChartOfAccountsProps> = ({
  onSave,
  serviceName,
  chartsReceivables,
  chartsPayables,
  currentChartReceivable,
  currentChartPayable,
  originalChartPayables,
  originalChartReceivable,
  onReceivableChange,
  onPayableChange,
}) => {
  const noChartOfAccountsReceivables = Object.values(chartsReceivables).length === 0;
  const noChartOfAccountsPayables = Object.values(chartsPayables).length === 0;
  const { t } = useTranslation('translations', { keyPrefix: 'AccountingSystem.AccountingSoftware.ChartOfAccount' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  return (
    <AccountingSoftwareLayout
      leftContent={
        <div className={styles.leftContainer}>
          <Box flx="col" base="8" gap="lg" jc="sa">
            <Title title={t('title')} />
            <p className={styles.paragraph}>{t('description')}</p>
          </Box>
        </div>
      }
      rightContent={
        <div className={styles.rightContainer}>
          <Box base="5" flx="col" gap="lg">
            <SubTitle subTitle={t('Form.title')} variant="small" />
            <Box flx="col">
              <Box flx="col">
                <h3 className={styles.label}>{t('Form.stepOne', { serviceName })}</h3>

                {serviceName && (
                  <img
                    className={styles.img}
                    src={AccountingImageMap[serviceName]?.src || ''}
                    alt={AccountingImageMap[serviceName]?.alt || ''}
                  />
                )}
              </Box>
              <Box flx="col" maxwidth="900px" pt="lg">
                <h3 className={styles.label}>{t('Form.stepTwo')}</h3>

                {noChartOfAccountsPayables && (
                  <div className={styles.paragraph}>{t('Form.noChartOfAccountsPayables')}</div>
                )}
                {!noChartOfAccountsPayables && (
                  <Select
                    name="chartofAccountsPayable"
                    variant="outlined"
                    placeholder={t('Form.selectStepPlaceholder')}
                    selectionAreaHeight="200px"
                    options={
                      Object.values(chartsPayables)
                        .map(
                          (charts: vp.ChartOfAccounts) =>
                            ({
                              id: charts.id,
                              name: charts.name,
                            }) as { id: string; name: string },
                        )
                        .reduce(
                          (result, item: { id: string; name: string }) => ({ ...result, [item.id]: item.name }),
                          {},
                        ) as {
                        [key: string]: string;
                      }
                    }
                    value={currentChartPayable}
                    onChange={(e) => {
                      onPayableChange(e.target.value);
                    }}
                    onBlur={() => {}}
                  />
                )}
              </Box>
              <Box flx="col" maxwidth="900px" pt="lg">
                <h3 className={styles.label}>{t('Form.stepThree')}</h3>

                {noChartOfAccountsReceivables && (
                  <div className={styles.paragraph}>{t('Form.noChartOfAccountsReceivables')}</div>
                )}
                {!noChartOfAccountsReceivables && (
                  <Select
                    name="chartofAccountsReceivables"
                    variant="outlined"
                    placeholder={t('Form.selectStepPlaceholder')}
                    selectionAreaHeight="200px"
                    options={
                      Object.values(chartsReceivables)
                        .map(
                          (charts: vp.ChartOfAccounts) =>
                            ({
                              id: charts.id,
                              name: charts.name,
                            }) as { id: string; name: string },
                        )
                        .reduce(
                          (result, item: { id: string; name: string }) => ({ ...result, [item.id]: item.name }),
                          {},
                        ) as {
                        [key: string]: string;
                      }
                    }
                    value={currentChartReceivable}
                    onChange={(e) => {
                      onReceivableChange(e.target.value);
                    }}
                    onBlur={() => {}}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </div>
      }
      bottomRow={
        <Box flx="row" jc="fe">
          <Button
            id="confirm-accounting-system"
            label={
              originalChartPayables === '' || originalChartReceivable === ''
                ? tGlobal('Buttons.confirm')
                : tGlobal('Buttons.update')
            }
            onClick={() => {
              onSave();
            }}
            isDisabled={
              noChartOfAccountsReceivables ||
              noChartOfAccountsPayables ||
              !currentChartPayable ||
              !currentChartReceivable ||
              (originalChartPayables === currentChartPayable && originalChartReceivable === currentChartReceivable)
            }
            height="36px"
          />
        </Box>
      }
    />
  );
};
export default ChartOfAccounts;
